import { useState, useEffect } from 'react';
import { apiService } from '../../lib/api';
import { useNotifications } from '../../hooks/useNotifications';

interface Log {
  timestamp: string;
  level: 'info' | 'error' | 'warn';
  message: string;
}

interface LogStats {
  total: number;
  byLevel: {
    info: number;
    warn: number;
    error: number;
  };
  lastHourCount: number;
  recentErrors: Log[];
}

export default function ServerLogs() {
  const { showSuccess, showError } = useNotifications();
  const [logs, setLogs] = useState<Log[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [filterLevel, searchQuery]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadLogs(true);
      loadStats();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, filterLevel, searchQuery]);

  const loadLogs = async (silent = false) => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '200');

      if (filterLevel !== 'all') {
        params.append('level', filterLevel);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await apiService.request<any>(`/api/v1/admin/logs?${params.toString()}`);

      if (response.success) {
        setLogs(response.data);
      }
    } catch (error: any) {
      if (!silent) {
        showError('Load Failed', error.message || 'Failed to load logs');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.request<any>('/api/v1/admin/logs/stats');
      if (response.success) {
        setStats(response.data);
      }
    } catch (error: any) {
      // Silently fail stats loading
      console.error('Failed to load log stats:', error);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all server logs?')) {
      return;
    }

    try {
      await apiService.request('/api/v1/admin/logs/clear', {
        method: 'POST',
      });
      showSuccess('Cleared', 'Server logs cleared successfully');
      loadLogs();
    } catch (error: any) {
      showError('Clear Failed', error.message || 'Failed to clear logs');
    }
  };

  const exportLogs = () => {
    const logsText = logs.map(log =>
      `[${new Date(log.timestamp).toLocaleString()}] [${log.level.toUpperCase()}] ${log.message}`
    ).join('\n');

    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `server-logs-${new Date().toISOString()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showSuccess('Exported', 'Server logs exported successfully');
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 theme-text-muted">Loading server logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold theme-text">Server Logs</h1>
          <p className="text-sm theme-text-muted mt-1">
            Real-time backend server logs and events
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { loadLogs(); loadStats(); }}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={exportLogs}
            disabled={logs.length === 0}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            Export
          </button>
          <button
            onClick={handleClearLogs}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            Clear Logs
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="theme-card p-4">
            <div className="text-sm theme-text-muted mb-1">Total Logs</div>
            <div className="text-2xl font-bold theme-text">{stats.total}</div>
          </div>
          <div className="theme-card p-4">
            <div className="text-sm theme-text-muted mb-1">Info</div>
            <div className="text-2xl font-bold text-green-600">{stats.byLevel.info}</div>
          </div>
          <div className="theme-card p-4">
            <div className="text-sm theme-text-muted mb-1">Warnings</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.byLevel.warn}</div>
          </div>
          <div className="theme-card p-4">
            <div className="text-sm theme-text-muted mb-1">Errors</div>
            <div className="text-2xl font-bold text-red-600">{stats.byLevel.error}</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="theme-card mb-4 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium theme-text mb-2 block">Search Logs</label>
            <input
              type="text"
              placeholder="Search log messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border rounded theme-input text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium theme-text mb-2 block">Filter Level</label>
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="w-full px-3 py-2 border rounded theme-input text-sm"
            >
              <option value="all">All Levels</option>
              <option value="info">Info</option>
              <option value="warn">Warnings</option>
              <option value="error">Errors</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="auto-refresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="auto-refresh" className="text-sm theme-text">
              Auto-refresh (5s)
            </label>
          </div>

          <div className="text-sm theme-text-muted">
            Showing {logs.length} {logs.length === 1 ? 'log' : 'logs'}
            {stats && stats.lastHourCount > 0 && (
              <span className="ml-2">• {stats.lastHourCount} in last hour</span>
            )}
          </div>
        </div>
      </div>

      {/* Logs Display */}
      <div className="theme-card">
        <div className="bg-gray-900 rounded-lg p-4 max-h-[600px] overflow-y-auto font-mono text-xs">
          {logs.length === 0 ? (
            <div className="text-gray-400 text-center py-12">
              No logs available. Server logs will appear here.
            </div>
          ) : (
            <div className="space-y-1">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className={`py-1 px-2 rounded ${
                    log.level === 'error' ? 'bg-red-900/30' :
                    log.level === 'warn' ? 'bg-yellow-900/30' :
                    ''
                  }`}
                >
                  <span className="text-gray-500">
                    [{new Date(log.timestamp).toLocaleTimeString()}]
                  </span>
                  {' '}
                  <span className={`font-semibold ${
                    log.level === 'error' ? 'text-red-400' :
                    log.level === 'warn' ? 'text-yellow-400' :
                    'text-green-400'
                  }`}>
                    [{log.level.toUpperCase()}]
                  </span>
                  {' '}
                  <span className="text-gray-200">{log.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Panel */}
      <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Log Information</h3>
            <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
              <li>Logs are stored in server memory (last 500 entries)</li>
              <li>Logs are cleared on server restart</li>
              <li>Use filters to focus on specific log levels</li>
              <li>Enable auto-refresh to see logs in real-time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
